/** Member detail side-drawer (AM-03/08): profile, membership, check-ins, actions. */
import { requestFreeze } from '@gym/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, CreditCard, Mail, Phone, Snowflake } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TrendChart } from '../../components/charts/index.js';
import type { TrendPoint } from '../../components/charts/index.js';
import { Button } from '../../components/ui/Button.js';
import { SideDrawer } from '../../components/ui/Drawer.js';
import { Skeleton } from '../../components/ui/Feedback.js';
import { Field, Input, Textarea } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { Money, useFormatters } from '../../components/ui/Money.js';
import { StatusPill, statusTone } from '../../components/ui/StatusPill.js';
import { toast } from '../../components/ui/Toaster.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

import type { MemberRow } from './MembersPage.js';

interface PaymentRow {
  id: string;
  amount: number;
  kind: string;
  fiscal_status: string;
  paid_at: string;
}

function Row({ label, children }: { label: ReactNode; children: ReactNode }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm font-medium text-fg">{children}</span>
    </div>
  );
}

function Section({ title, children }: { title: ReactNode; children: ReactNode }): ReactNode {
  return (
    <div className="mb-5">
      <h4 className="mb-1 text-xs font-bold uppercase tracking-widest text-faint">{title}</h4>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

/** Bucket an array of ISO datetime strings by ISO-week start (Monday). Returns last N weeks. */
function bucketByWeek(dates: string[], weeks = 8): TrendPoint[] {
  const msPerDay = 86_400_000;
  const msPerWeek = 7 * msPerDay;

  // Compute Monday of a date's week
  function weekStart(d: Date): number {
    const day = d.getDay(); // 0=Sun..6=Sat
    const diff = (day === 0 ? -6 : 1 - day) * msPerDay;
    return Math.floor((d.getTime() + diff) / msPerWeek) * msPerWeek;
  }

  const now = Date.now();
  const todayWeekStart = weekStart(new Date(now));
  const buckets = new Map<number, number>();

  // Initialize last `weeks` buckets with 0
  for (let i = weeks - 1; i >= 0; i--) {
    buckets.set(todayWeekStart - i * msPerWeek, 0);
  }

  for (const iso of dates) {
    const ts = weekStart(new Date(iso));
    if (buckets.has(ts)) {
      buckets.set(ts, (buckets.get(ts) ?? 0) + 1);
    }
  }

  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([ts, count]) => {
      const d = new Date(ts);
      const label = `${d.getMonth() + 1}/${d.getDate()}`;
      return { label, visits: count };
    });
}

export function MemberDetailDrawer({
  member,
  open,
  onOpenChange,
  onRecordPayment,
}: {
  member: MemberRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRecordPayment: (memberId: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';

  const [freezeOpen, setFreezeOpen] = useState(false);

  // ── Payments (lazy) ────────────────────────────────────────────────────────
  const payments = useQuery({
    queryKey: ['member_payments', tenantId, member?.id],
    enabled: !!member?.id && open,
    queryFn: async (): Promise<PaymentRow[]> => {
      const { data, error } = await supabase
        .from('payments')
        .select('id, amount, kind, fiscal_status, paid_at')
        .eq('member_id', member!.id)
        .order('paid_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as unknown as PaymentRow[];
    },
  });

  // ── Entrance frequency (lazy, longer window for bucketing) ─────────────────
  const entranceFreq = useQuery({
    queryKey: ['member_entrance_freq', member?.id],
    enabled: !!member?.id && open,
    queryFn: async (): Promise<{ checked_in_at: string }[]> => {
      const { data, error } = await supabase
        .from('checkins')
        .select('checked_in_at')
        .eq('member_id', member!.id)
        .order('checked_in_at', { ascending: false })
        .limit(180);
      if (error) throw error;
      return (data ?? []) as unknown as { checked_in_at: string }[];
    },
  });

  if (!member) return null;
  const fullName = `${member.first_name} ${member.last_name}`;

  // Build entrance-frequency chart data
  const freqDates = (entranceFreq.data ?? []).map((r) => r.checked_in_at);
  const chartData = bucketByWeek(freqDates, 8);
  const hasEntrances = freqDates.length > 0;

  return (
    <>
      <SideDrawer
        open={open}
        onOpenChange={onOpenChange}
        width="lg"
        title={fullName}
        subtitle={
          member.membership_status ? (
            <StatusPill
              status={member.membership_status}
              tone={statusTone(member.membership_status)}
              label={t(`memberships.status.${member.membership_status}`, {
                defaultValue: member.membership_status,
              })}
            />
          ) : (
            t('memberships.none')
          )
        }
        footer={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              icon={<CreditCard className="h-4 w-4" />}
              onClick={() => onRecordPayment(member.id)}
            >
              {t('billing.recordPayment')}
            </Button>
            <Button
              variant="secondary"
              icon={<CalendarPlus className="h-4 w-4" />}
              onClick={() => navigate(`/memberships?member=${member.id}`)}
            >
              {t('memberships.renew')}
            </Button>
            <Button
              variant="secondary"
              icon={<Snowflake className="h-4 w-4" />}
              disabled={!member.active_membership_id}
              onClick={() => setFreezeOpen(true)}
            >
              {t('memberships.freeze')}
            </Button>
          </div>
        }
      >
        {/* ── Profile ──────────────────────────────────────────────────────── */}
        <Section title={t('members.profile')}>
          <Row
            label={
              <span className="inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                {t('members.fields.phone')}
              </span>
            }
          >
            {member.phone ?? '—'}
          </Row>
          <Row
            label={
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                {t('members.fields.email')}
              </span>
            }
          >
            {member.email ?? '—'}
          </Row>
          <Row label={t('members.fields.gender')}>
            {member.gender
              ? t(`members.gender.${member.gender}`, { defaultValue: member.gender })
              : '—'}
          </Row>
          <Row label={t('members.fields.dob')}>
            {member.date_of_birth ? fmt.date(member.date_of_birth) : '—'}
          </Row>
          <Row label={t('members.joinDate')}>{fmt.date(member.join_date)}</Row>
          <Row label={t('members.longevity')}>
            {member.longevity_months != null
              ? t('members.monthsCount', { count: member.longevity_months })
              : '—'}
          </Row>
        </Section>

        {/* ── Plan status (consolidates the old "Memberships" section) ──────── */}
        <Section title={t('members.planStatus')}>
          <Row label={t('memberships.plan')}>
            {member.plan_type
              ? t(`plans.type.${member.plan_type}`, { defaultValue: member.plan_type })
              : t('memberships.none')}
          </Row>
          <Row label={t('memberships.endDate')}>
            {member.end_date ? fmt.date(member.end_date) : '—'}
          </Row>
          {member.credits_remaining != null ? (
            <Row label={t('memberships.creditsRemaining')}>{member.credits_remaining}</Row>
          ) : null}
        </Section>

        {/* ── Payments ─────────────────────────────────────────────────────── */}
        <Section title={t('members.paymentsSection')}>
          {payments.isLoading ? (
            <div className="space-y-2 py-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : (payments.data ?? []).length === 0 ? (
            <p className="py-3 text-sm text-faint">—</p>
          ) : (
            (payments.data ?? []).map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 py-2">
                <span className="text-sm text-muted">
                  {fmt.date(p.paid_at, { dateStyle: 'medium' })}
                </span>
                <span className="text-sm text-fg">
                  {t(`analytics.kind.${p.kind}`, { defaultValue: p.kind })}
                </span>
                <Money minor={p.amount} />
                <StatusPill
                  status={p.fiscal_status}
                  tone={statusTone(p.fiscal_status)}
                  label={t(`billing.fiscal.${p.fiscal_status}`, { defaultValue: p.fiscal_status })}
                />
              </div>
            ))
          )}
        </Section>

        {/* ── Entrance frequency ────────────────────────────────────────────── */}
        <Section title={t('members.entranceFrequency')}>
          {entranceFreq.isLoading ? (
            <div className="space-y-2 py-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : !hasEntrances ? (
            <p className="py-3 text-sm text-faint">{t('members.noCheckins')}</p>
          ) : (
            <div className="h-28 py-2">
              <TrendChart
                data={chartData}
                series={[{ key: 'visits', name: t('members.entranceFrequency') }]}
              />
            </div>
          )}
        </Section>

        {/* ── Recent check-ins list ─────────────────────────────────────────── */}
        <Section title={t('members.recentCheckins')}>
          {entranceFreq.isLoading ? (
            <div className="space-y-2 py-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : !hasEntrances ? (
            <p className="py-3 text-sm text-faint">{t('members.noCheckins')}</p>
          ) : (
            (entranceFreq.data ?? []).slice(0, 8).map((c, idx) => (
              <div
                key={`${c.checked_in_at}-${idx}`}
                className="flex items-center justify-between gap-3 py-2"
              >
                <span className="text-sm text-fg">
                  {fmt.date(c.checked_in_at, { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
              </div>
            ))
          )}
        </Section>
      </SideDrawer>

      {member.active_membership_id ? (
        <FreezeModal
          open={freezeOpen}
          onOpenChange={setFreezeOpen}
          membershipId={member.active_membership_id}
          memberName={fullName}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['members'] });
            onOpenChange(false);
          }}
        />
      ) : null}
    </>
  );
}

function FreezeModal({
  open,
  onOpenChange,
  membershipId,
  memberName,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  membershipId: string;
  memberName: string;
  onDone: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      requestFreeze(supabase, membershipId, { start_date: start, end_date: end, reason }),
    onSuccess: () => {
      toast.success(t('memberships.freezeRequested'), memberName);
      onOpenChange(false);
      onDone();
    },
    onError: (e: Error) => toast.error(t('common.error'), e.message),
  });

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('memberships.freeze')}
      description={memberName}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!start || !end}
          >
            {t('memberships.freeze')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('memberships.startDate')}>
          {(id) => (
            <Input id={id} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          )}
        </Field>
        <Field label={t('memberships.endDate')}>
          {(id) => (
            <Input id={id} type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          )}
        </Field>
      </div>
      <Field label={t('memberships.freezeReason')} className="mt-3">
        {(id) => (
          <Textarea id={id} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        )}
      </Field>
    </Modal>
  );
}
